// import React, { useEffect, useState } from 'react'
import React from 'react'
import { useRef, useState, useCallback, useEffect } from "react"
import { Swiper, SwiperSlide } from '@components/molecules/Slider'
import { Pagination, Navigation } from "swiper/modules"
import Icon from '@components/atoms/Icon'
import type { Swiper as SwiperInstance, SwiperEvents } from 'swiper/types'

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

interface UgcMessage {
  copy: string,
  caption: string,
  video: string,
  url: string,
  poster?: string
}

export interface UgcData {
  ugc_items: UgcMessage[]
}

interface UgcSlideProps extends UgcMessage {
  onPlaybackChange?: (isPlaying: boolean) => void
  onVideoPlay?: (video?: HTMLVideoElement | null) => void
}

const AUTOPLAY_DELAY = 3000

const UgcSlide: React.FC<UgcSlideProps> = ({
  copy,
  caption,
  video,
  url,
  poster,
  onPlaybackChange,
  onVideoPlay
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play().catch(() => {/* ignore */})
    } else {
      video.pause()
    }
  }, [])

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      togglePlay()
    }
  }

  useEffect(() => {
    if (!poster || typeof window === 'undefined') return
    const preload = new Image()
    preload.src = poster
  }, [poster])

  useEffect(() => {
    return () => {
      if (isPlaying) {
        onPlaybackChange?.(false)
      }
    }
  }, [isPlaying, onPlaybackChange])

  return (
    <div className="p-5 lg:p-0">
      <div
        className={`video-container relative cursor-pointer h-[478px] ${isPlaying ? 'is-playing' : 'is-paused'}`}
        onClick={togglePlay}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
        aria-pressed={isPlaying}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}>
        <video
          ref={videoRef}
          data-ugc-video="true"
          loop
          playsInline
          preload="metadata"
          poster={poster}
          className="image h-full w-full object-cover aspect-[9/16] rounded"
          onPlay={() => {
            setIsPlaying((prev) => {
              if (!prev) {
                onPlaybackChange?.(true)
                onVideoPlay?.(videoRef.current)
              }
              return true
            })
          }}
          onPause={() => {
            setIsPlaying((prev) => {
              if (prev) {
                onPlaybackChange?.(false)
              }
              return false
            })
          }}>
          <source src={video} type="video/mp4" />
        </video>
        <span className="absolute top-[10%] w-full mx-auto text-center capitalize z-1 [-webkit-text-stroke:0.7px_black] text-base text-white font-extrabold">{caption}</span>
      </div>

      <div className="ugc-bottom flex items-center pt-5 overflow-visible">
        <div className="product-title-stars">
          <div className="stars">
              <Icon name="stars" aria-hidden="true" className="w-[80px]" />
          </div>
          <div className="product-title text-xl font-appleGaramond">
            <span>{copy}</span>
          </div>
        </div>
        <a href={url} className="ml-auto" aria-label={copy}>
          <button className="px-6 py-2 rounded-lg border bg-white font-bold shadow-[0px_7px_25px_-20px_rgba(0,0,0,0.65)]">
            <span>View</span>
          </button>
        </a>
      </div>
    </div>
  )
}

const UgcModule: React.FC<UgcData> = ({ ugc_items }) => {
  const swiperRef = useRef<SwiperInstance | null>(null)
  const swiperHandlersRef = useRef<Array<{ event: keyof SwiperEvents, handler: (...args: any[]) => void }>>([])
  const moduleRef = useRef<HTMLDivElement | null>(null)
  const autoplayTimerRef = useRef<number | null>(null)
  const hasActiveVideoRef = useRef(false)

  const clearAutoplayTimer = useCallback(() => {
    if (autoplayTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(autoplayTimerRef.current)
    }
    autoplayTimerRef.current = null
  }, [])

  const queueAutoplay = useCallback(() => {
    if (typeof window === 'undefined') return
    if (hasActiveVideoRef.current) return
    if (!swiperRef.current) return

    clearAutoplayTimer()
    autoplayTimerRef.current = window.setTimeout(() => {
      const swiperInstance = swiperRef.current
      if (swiperInstance && !hasActiveVideoRef.current) {
        swiperInstance.slideNext()
      }
      queueAutoplay()
    }, AUTOPLAY_DELAY)
  }, [clearAutoplayTimer])

  const registerSwiperHandler = useCallback(<K extends keyof SwiperEvents>(
    swiper: SwiperInstance,
    event: K,
    handler: SwiperEvents[K]
  ) => {
    swiper.on(event, handler)
    swiperHandlersRef.current.push({
      event,
      handler: handler as unknown as (...args: any[]) => void
    })
  }, [])

  const detachSwiperHandlers = useCallback(() => {
    const swiperInstance = swiperRef.current
    if (!swiperInstance) return
    swiperHandlersRef.current.forEach(({ event, handler }) => {
      swiperInstance.off(event, handler)
    })
    swiperHandlersRef.current = []
  }, [])
  const handleMouseEnter = useCallback(() => {
    clearAutoplayTimer()
  }, [clearAutoplayTimer])

  const handleMouseLeave = useCallback(() => {
    if (!hasActiveVideoRef.current) {
      queueAutoplay()
    }
  }, [queueAutoplay])
  const pauseAllExcept = useCallback((videoToKeep?: HTMLVideoElement | null) => {
    const root = moduleRef.current
    if (!root) return

    const videos = root.querySelectorAll<HTMLVideoElement>('video[data-ugc-video]')
    videos.forEach((videoEl) => {
      if (videoEl === videoToKeep) return
      if (!videoEl.paused) {
        videoEl.pause()
      }
    })
  }, [])

  const handleVideoPlay = useCallback((videoEl?: HTMLVideoElement | null) => {
    pauseAllExcept(videoEl ?? null)
  }, [pauseAllExcept])

  const handleVideoPlaybackChange = useCallback((isPlaying: boolean) => {
    hasActiveVideoRef.current = isPlaying
    if (isPlaying) {
      clearAutoplayTimer()
    } else {
      queueAutoplay()
    }
  }, [clearAutoplayTimer, queueAutoplay])

  const pauseVideosOutsideVisible = useCallback((swiper: SwiperInstance) => {
    swiper.slides?.forEach((slideEl) => {
      if (!slideEl.classList.contains('swiper-slide-visible')) {
        const videoEl = slideEl.querySelector<HTMLVideoElement>('video[data-ugc-video]')
        if (videoEl && !videoEl.paused) {
          videoEl.pause()
        }
      }
    })
  }, [])

  const handleSlideChange = useCallback((swiper: SwiperInstance) => {
    pauseVideosOutsideVisible(swiper)

    if (typeof window !== 'undefined') {
      const isMobile = window.matchMedia('(max-width: 767px)').matches
      if (isMobile) {
        const activeSlide = swiper.slides?.[swiper.activeIndex]
        const activeVideo = activeSlide?.querySelector<HTMLVideoElement>('video[data-ugc-video]')
        pauseAllExcept(activeVideo ?? null)
      }
    }

    if (hasActiveVideoRef.current) {
      clearAutoplayTimer()
    } else {
      queueAutoplay()
    }
  }, [pauseAllExcept, pauseVideosOutsideVisible, clearAutoplayTimer, queueAutoplay])

  useEffect(() => {
    return () => {
      clearAutoplayTimer()
      detachSwiperHandlers()
    }
  }, [clearAutoplayTimer, detachSwiperHandlers])

  return (
    <div className={`h-fit relative`} ref={moduleRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <>
        <Swiper
          modules={[Pagination, Navigation]}
          spaceBetween={20}
          slidesPerView={1}
          loop={true}
          className="h-[580px]"
          onSwiper={(swiper) => {
            detachSwiperHandlers()
            swiperRef.current = swiper
            const slideChangeHandler = () => handleSlideChange(swiper)
            registerSwiperHandler(swiper, 'slideChange', slideChangeHandler)

            const pauseHandler = () => clearAutoplayTimer()
            const resumeHandler = () => {
              if (!hasActiveVideoRef.current) {
                queueAutoplay()
              }
            }

            registerSwiperHandler(swiper, 'touchStart', pauseHandler)
            registerSwiperHandler(swiper, 'touchEnd', resumeHandler)
            registerSwiperHandler(swiper, 'transitionEnd', resumeHandler)

            queueAutoplay()
          }}

          pagination={{
            el: '.ugc-pagination',
            type: 'bullets',
            clickable: true
          }}

          navigation={{
            nextEl: '.carousel-button-next',
            prevEl: '.carousel-button-prev',
          }}

          breakpoints={{
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024:{ slidesPerView: 4 },
            1280:{ slidesPerView: 5 }
          }}
        >
          {ugc_items.map((message: UgcMessage, index: number) => {
            const { copy, caption, video, url, poster } = message
            return (
              <SwiperSlide key={`${copy}-${index}`}>
              <UgcSlide
                copy={copy}
                caption={caption}
                video={video}
                url={url}
                poster={poster}
                onPlaybackChange={handleVideoPlaybackChange}
                onVideoPlay={handleVideoPlay}
              />
              </SwiperSlide>
            )
          })}
        </Swiper>

        <div className="flex items-center justify-center space-x-4" role="group" aria-label="Carousel controls">
          <button
            type="button"
            className="carousel-button-prev flex-shrink-0 flex justify-center items-center cursor-pointer bg-white/50 border-none text-center font-extrabold rounded-full w-11 h-11 p-3.75"
            aria-label="Previous slide"
            tabIndex={0}
          >
            <Icon name="carousel-arrow-left" aria-hidden="true" />
          </button>

          <div className="ugc-pagination text-center" role="region" aria-label="Carousel pagination"></div>

          <button
            type="button"
            className="carousel-button-next flex-shrink-0 flex justify-center items-center cursor-pointer bg-white/50 border-none text-center font-extrabold rounded-full w-11 h-11 p-3.75"
            aria-label="Next slide"
            tabIndex={0}
          >
            <Icon name="carousel-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </>
    </div>
  )
}

export default UgcModule
